import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  FileText, CheckCircle2, Clock, XCircle, RefreshCw, ArrowRight, ArrowUpRight,
  Building2, Zap, TrendingUp, TrendingDown, ExternalLink, Sparkles,
  Users, BedDouble, DollarSign, Bell, Activity, BarChart3, Receipt, Send,
  QrCode, Info, RotateCw, ShieldCheck, Download, AlertTriangle, Loader2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

/* ── Stat Card with Gradient ── */
function StatCard({ title, value, subtitle, icon: Icon, color, trend, onClick }) {
  const gradients = {
    blue: 'from-blue-500 to-indigo-600',
    amber: 'from-amber-500 to-orange-600',
    emerald: 'from-emerald-500 to-teal-600',
    red: 'from-red-500 to-rose-600',
    purple: 'from-purple-500 to-pink-600',
  };

  return (
    <Card 
      className="group relative overflow-hidden border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20 hover:shadow-xl hover:shadow-slate-300/50 dark:hover:shadow-black/40 transition-all duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradients[color]} opacity-[0.03] group-hover:opacity-[0.06] transition-opacity`} />
      <CardContent className="p-5 relative">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{value}</p>
            {subtitle && <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradients[color]} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`mt-3 flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{trend >= 0 ? '+' : ''}{trend}% from yesterday</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activePropertyName, setActivePropertyName] = useState(null);
  const [stats, setStats] = useState({ total: 0, validated: 0, pending: 0, rejected: 0, failed: 0, submitted: 0, revenue_month: 0, revenue_today: 0, today: 0, qr_submissions: 0, qr_converted: 0, lhdn_last_sync: null, lhdn_last_status: null });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [pmsConnections, setPmsConnections] = useState([]);
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sstCorrection, setSstCorrection] = useState(null);
  const [downloadingSst, setDownloadingSst] = useState(false);
  const [sstPending, setSstPending] = useState(null);
  const [confirmingSst, setConfirmingSst] = useState(false);
  const [applyingSst, setApplyingSst] = useState(false);

  const token = localStorage.getItem('einvoice_token');

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadDashboardData();
    const handler = () => loadDashboardData();
    window.addEventListener('property-changed', handler);
    return () => window.removeEventListener('property-changed', handler);
  }, []);

  const loadDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const { data: profileData } = await portalApi.get('/api/einvoice/portal/auth/me', { headers });
      setUser(profileData);
      if (!profileData.onboarding_completed) { navigate('/einvoice/portal/onboarding'); return; }

      const { data: propsData } = await portalApi.get('/api/einvoice/portal/properties', { headers });
      const props = propsData.properties || [];
      setProperties(props);

      const selectedId = localStorage.getItem('einvoice_selected_property');
      const targetProp = selectedId ? props.find(p => p.id === selectedId) : props[0];
      setActivePropertyName(selectedId ? (targetProp?.property_name || null) : null);
      if (targetProp) {
        const url = selectedId
          ? `/api/einvoice/dashboard?property_id=${targetProp.id}`
          : `/api/einvoice/dashboard`;
        const { data } = await portalApi.get(url, { headers });
        setStats(data.stats || {});
        setRecentInvoices(data.recent_invoices || []);
        setDailyTrend(data.daily_trend || []);
      }

      try {
        const { data: pmsData } = await portalApi.get('/api/einvoice/portal/pms/connections', { headers });
        setPmsConnections(pmsData.connections || []);
      } catch (e) { /* optional */ }

      // Check automation settings
      try {
        const { data: autoData } = await portalApi.get('/api/einvoice/automation/settings', { headers });
        setAutomationEnabled(autoData.auto_import_enabled || false);
      } catch (e) { /* optional */ }
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('einvoice_token'); navigate('/einvoice/portal/auth'); }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastUpdated(new Date());
    }
    // SST status is loaded separately (non-blocking) so it never delays the dashboard
    loadSstStatus();
  };

  // Non-blocking: fetch SST correction status / pending count after the dashboard renders
  const loadSstStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const selId = localStorage.getItem('einvoice_selected_property');
      const scope = selId ? `?property_id=${selId}` : '';
      const { data: sstData } = await portalApi.get(`/api/einvoice/gateway/sst-correction/summary${scope}`, { headers });
      if (sstData && sstData.corrected) {
        setSstCorrection(sstData);
        setSstPending(null);
      } else {
        setSstCorrection(null);
        try {
          const { data: dry } = await portalApi.post(`/api/einvoice/gateway/sst-correction/run-backfill${scope}`, {}, { headers });
          setSstPending(dry && dry.would_update > 0 ? dry : null);
        } catch (e) { setSstPending(null); }
      }
    } catch (e) { /* optional, non-blocking */ }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const handleDownloadSstAudit = async () => {
    setDownloadingSst(true);
    try {
      const selId = localStorage.getItem('einvoice_selected_property');
      const url = selId
        ? `/api/einvoice/gateway/sst-correction/export-csv?property_id=${selId}`
        : `/api/einvoice/gateway/sst-correction/export-csv`;
      const res = await portalApi.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'sst-correction-audit.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      /* swallow — button stays available */
    } finally {
      setDownloadingSst(false);
    }
  };

  const handleApplySst = async () => {
    setApplyingSst(true);
    try {
      const selId = localStorage.getItem('einvoice_selected_property');
      const scope = selId ? `&property_id=${selId}` : '';
      const { data } = await portalApi.post(
        `/api/einvoice/gateway/sst-correction/run-backfill?commit=true${scope}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`SST corrected on ${data.updated || 0} invoice(s).`);
      setConfirmingSst(false);
      setSstPending(null);
      loadDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply SST correction. Please try again.');
    } finally {
      setApplyingSst(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
            <p className="text-sm text-slate-500 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </PortalLayout>
    );
  }

  const pmsConnected = pmsConnections.some(c => c.status === 'connected');
  const successRate = stats.total > 0 ? Math.round((stats.validated / stats.total) * 100) : 0;

  const setupSteps = [
    { label: 'Add your property', desc: 'Create your first property to start invoicing.', done: properties.length > 0, link: '/einvoice/portal/properties' },
    { label: 'Connect your PMS', desc: 'Auto-sync bookings from your channel manager.', done: pmsConnected, link: '/einvoice/portal/integrations' },
    { label: 'Connect LHDN MyInvois', desc: 'Link your LHDN API for compliant e-Invoices.', done: stats.total > 0, link: '/einvoice/portal/settings' },
    { label: 'Submit your first e-Invoice', desc: 'Generate and validate an invoice with LHDN.', done: stats.validated > 0, link: '/einvoice/portal/invoices' },
  ];
  const setupDone = setupSteps.filter(s => s.done).length;
  const setupPct = Math.round((setupDone / setupSteps.length) * 100);
  const currentStepIdx = setupSteps.findIndex(s => !s.done);

  const donutData = [
    { label: 'Validated', value: stats.validated, color: '#10b981' },
    { label: 'Failed', value: (stats.failed || 0) + (stats.rejected || 0), color: '#ef4444' },
    { label: 'Pending', value: stats.pending, color: '#f59e0b' },
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8" data-testid="portal-dashboard">
        {/* ─── Welcome Header ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white" data-testid="dashboard-welcome">
                {getGreeting()}, {user?.full_name?.split(' ')[0]}
              </h1>
              <Sparkles className="w-6 h-6 text-amber-400" />
            </div>
            <p className="text-slate-500 dark:text-gray-400">Here's what's happening with your e-invoices today</p>
            <div className="flex items-center gap-2 mt-1.5">
              <Building2 className="w-3.5 h-3.5 text-[#4169E1]" />
              <span className="text-xs font-medium text-[#4169E1]" data-testid="viewing-property-label">
                Viewing: {activePropertyName || 'All Properties'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-data-btn"
              className="text-slate-600 dark:text-gray-300 border-slate-200 dark:border-zinc-700"
            >
              <RotateCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
            <Button 
              onClick={() => navigate('/einvoice/portal/invoices')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25"
            >
              <Send className="w-4 h-4 mr-2" /> Submit Invoices
            </Button>
          </div>
        </div>

        {/* Last updated timestamp */}
        {lastUpdated && (
          <p className="text-[11px] text-slate-400 dark:text-gray-500 -mt-6" data-testid="last-updated">
            Last updated: {lastUpdated.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
        )}

        {/* ─── SST Correction Evidence Banner ─── */}
        {sstCorrection && (
          <div
            className="relative overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 p-5"
            data-testid="sst-correction-banner"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shrink-0">
                  <ShieldCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-emerald-900 dark:text-emerald-200">Inclusive-SST Historical Correction Applied</h3>
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0" data-testid="sst-corrected-badge">
                      SST Corrected
                    </Badge>
                  </div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300/80 mt-1" data-testid="sst-correction-detail">
                    {sstCorrection.corrected_count} invoice{sstCorrection.corrected_count === 1 ? '' : 's'} recalculated to tax-inclusive SST.
                    {' '}SST adjusted from RM {(sstCorrection.total_old_tax || 0).toFixed(2)} → RM {(sstCorrection.total_new_tax || 0).toFixed(2)}
                    {' '}(Δ RM {(sstCorrection.total_tax_delta || 0).toFixed(2)}).
                    {sstCorrection.last_corrected_at ? ` Last applied ${new Date(sstCorrection.last_corrected_at).toLocaleDateString('en-MY')}.` : ''}
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDownloadSstAudit}
                disabled={downloadingSst}
                data-testid="download-sst-audit-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25 shrink-0"
              >
                <Download className={`w-4 h-4 mr-2 ${downloadingSst ? 'animate-pulse' : ''}`} />
                {downloadingSst ? 'Preparing…' : 'Download Audit CSV'}
              </Button>
            </div>
          </div>
        )}

        {/* ─── SST Correction Available (one-click apply) ─── */}
        {!sstCorrection && sstPending && (
          <div
            className="relative overflow-hidden rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 p-5"
            data-testid="sst-pending-banner"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">Inclusive-SST Correction Available</h3>
                  <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-1" data-testid="sst-pending-detail">
                    {sstPending.would_update} invoice{sstPending.would_update === 1 ? '' : 's'} still calculate SST the old way. Apply the one-time correction to recalculate them as tax-inclusive (8% within the gross), matching LHDN. Only affects draft/pending invoices — never submitted ones.
                  </p>
                </div>
              </div>
              {!confirmingSst ? (
                <Button
                  onClick={() => setConfirmingSst(true)}
                  data-testid="apply-sst-correction-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-500/25 shrink-0"
                >
                  Apply SST Correction
                </Button>
              ) : (
                <div className="flex items-center gap-2 shrink-0" data-testid="sst-confirm-actions">
                  <span className="text-xs font-medium text-amber-900 dark:text-amber-200 mr-1">Correct {sstPending.would_update}?</span>
                  <Button
                    onClick={handleApplySst}
                    disabled={applyingSst}
                    data-testid="confirm-sst-correction-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  >
                    {applyingSst ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {applyingSst ? 'Applying…' : 'Confirm'}
                  </Button>
                  <Button
                    onClick={() => setConfirmingSst(false)}
                    disabled={applyingSst}
                    variant="outline"
                    data-testid="cancel-sst-correction-btn"
                    className="border-amber-300 text-amber-800 dark:text-amber-200 shrink-0"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── KPI Cards ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="kpi-cards">
          <StatCard
            title="eInvoices Submitted Today"
            value={stats.today || 0}
            subtitle={`${stats.this_month || 0} this month (MTD)`}
            icon={Receipt}
            color="blue"
            onClick={() => navigate('/einvoice/portal/invoices')}
          />
          <StatCard
            title="eInvoices Issued (MTD)"
            value={stats.this_month || 0}
            subtitle="Month-to-date, excl. cancelled"
            icon={FileText}
            color="purple"
            onClick={() => navigate('/einvoice/portal/invoices')}
          />
          <StatCard
            title="Pending Submission"
            value={stats.pending}
            subtitle="awaiting LHDN"
            icon={Clock}
            color="amber"
            onClick={() => navigate('/einvoice/portal/invoices?status=pending')}
          />
          <StatCard
            title="eInvoice Requests (QR)"
            value={stats.qr_submissions || 0}
            subtitle={`${stats.qr_converted || 0} converted to invoices`}
            icon={QrCode}
            color="emerald"
            onClick={() => navigate('/einvoice/portal/qr')}
          />
        </div>

        {/* ─── eInvoice Status Overview ─── */}
        <div className="grid grid-cols-3 gap-4" data-testid="status-overview">
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-md">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1">
                  Submitted
                  <span className="relative group">
                    <Info className="w-3 h-3 text-slate-400 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                      Status synced from LHDN system in real-time
                    </span>
                  </span>
                </p>
                <p className="text-2xl font-bold text-blue-600">{stats.submitted || 0}</p>
              </div>
              <Send className="w-6 h-6 text-blue-400 opacity-50" />
            </CardContent>
          </Card>
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-md">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1">
                  Validated (LHDN)
                  <span className="relative group">
                    <Info className="w-3 h-3 text-slate-400 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                      Status synced from LHDN system in real-time
                    </span>
                  </span>
                </p>
                <p className="text-2xl font-bold text-emerald-600">{stats.validated || 0}</p>
              </div>
              <CheckCircle2 className="w-6 h-6 text-emerald-400 opacity-50" />
            </CardContent>
          </Card>
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-md">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1">
                  Rejected
                  <span className="relative group">
                    <Info className="w-3 h-3 text-slate-400 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                      Status synced from LHDN system in real-time
                    </span>
                  </span>
                </p>
                <p className="text-2xl font-bold text-red-600">{(stats.rejected || 0) + (stats.failed || 0)}</p>
              </div>
              <XCircle className="w-6 h-6 text-red-400 opacity-50" />
            </CardContent>
          </Card>
        </div>

        {/* ─── Main Content Grid ─── */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Chart Section */}
          <Card className="lg:col-span-2 border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20" data-testid="weekly-chart-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">eInvoice Revenue (Last 7 Days)</CardTitle>
                    <p className="text-xs text-slate-400 dark:text-gray-500">Based on: Invoice Amount (Issued)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Invoices
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[240px] mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorInvoices" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: 'none', 
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        padding: '12px 16px'
                      }}
                      formatter={(value) => [value, 'Invoices']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="invoices" 
                      stroke="#3b82f6" 
                      strokeWidth={3} 
                      fill="url(#colorInvoices)" 
                      dot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 7, fill: '#3b82f6', stroke: '#fff', strokeWidth: 3 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Revenue Summary */}
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800 grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-4">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">Today's Revenue</p>
                  <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                    RM {(stats.revenue_today || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-emerald-500/70 dark:text-emerald-400/50 mt-0.5">Based on: Invoice Amount (Issued)</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">Monthly Revenue (MTD)</p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    RM {(stats.revenue_month || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-blue-500/70 dark:text-blue-400/50 mt-0.5">Based on: Invoice Amount (Issued)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MyInvois Status Card */}
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20" data-testid="lhdn-integration-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">LHDN Sync Status</CardTitle>
                    <p className="text-xs text-slate-400 dark:text-gray-500">MyInvois e-Invoice integration</p>
                  </div>
                </div>
                <Badge className="bg-emerald-500 text-white border-0 animate-pulse">LIVE</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Sync Timestamps */}
              <div className="mb-3 p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg space-y-1.5" data-testid="lhdn-sync-info">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 dark:text-gray-400">Connection:</span>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">Connected</Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 dark:text-gray-400">Last synced:</span>
                  <span className="text-slate-700 dark:text-gray-300 font-medium" data-testid="last-sync-time">
                    {stats.lhdn_last_sync ? new Date(stats.lhdn_last_sync).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 dark:text-gray-400">Last submission:</span>
                  <span className={`font-medium ${stats.lhdn_last_status === 'success' ? 'text-emerald-600' : stats.lhdn_last_status === 'error' ? 'text-red-500' : 'text-slate-600'}`} data-testid="last-submission-status">
                    {stats.lhdn_last_status ? stats.lhdn_last_status.charAt(0).toUpperCase() + stats.lhdn_last_status.slice(1) : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="space-y-3">
                {donutData.map(d => (
                  <div key={d.label} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-sm text-slate-600 dark:text-gray-300">{d.label}</span>
                    </span>
                    <span className="text-sm font-bold text-slate-800 dark:text-white">{d.value}</span>
                  </div>
                ))}
              </div>

              <Button 
                variant="outline" 
                className="w-full mt-4 border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-zinc-800"
                onClick={() => navigate('/einvoice/portal/reports')}
              >
                View Reports <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ─── Recent Invoices & Setup ─── */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Invoices */}
          <Card className="lg:col-span-2 border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20" data-testid="recent-invoices-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">Recent Invoices</CardTitle>
                  <p className="text-xs text-slate-400 dark:text-gray-500">Latest submissions</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/einvoice/portal/invoices')} 
                className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                View All <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentInvoices.length > 0 ? (
                <div className="space-y-3">
                  {recentInvoices.slice(0, 5).map(inv => (
                    <div 
                      key={inv.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                      onClick={() => navigate('/einvoice/portal/invoices')}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          inv.status === 'valid' || inv.status === 'validated' 
                            ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                            : inv.status === 'rejected' || inv.status === 'error'
                              ? 'bg-red-100 dark:bg-red-900/30'
                              : 'bg-amber-100 dark:bg-amber-900/30'
                        }`}>
                          {inv.status === 'valid' || inv.status === 'validated' ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          ) : inv.status === 'rejected' || inv.status === 'error' ? (
                            <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">{inv.invoice_number}</p>
                          <p className="text-xs text-slate-400 dark:text-gray-500">{inv.guest_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-800 dark:text-white">RM {(inv.total_amount || 0).toFixed(2)}</p>
                        <p className="text-[11px] text-slate-400 dark:text-gray-500">{inv.invoice_date || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-slate-300 dark:text-zinc-600" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-gray-300 mb-1">No invoices yet</p>
                  <p className="text-xs text-slate-400 dark:text-gray-500">Import bookings from your PMS to get started</p>
                  <Button 
                    size="sm" 
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => navigate('/einvoice/portal/invoices')}
                  >
                    Create First Invoice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Setup Progress */}
          <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20" data-testid="setup-progress-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">Setup Progress</CardTitle>
                    <p className="text-xs text-slate-400 dark:text-gray-500">
                      {setupDone === setupSteps.length ? "You're all set — nicely done!" : `${setupDone} of ${setupSteps.length} steps complete`}
                    </p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="setup-progress-pct">{setupPct}%</span>
              </div>
              {/* Linear progress bar */}
              <div className="mt-3 h-2 w-full rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-700" style={{ width: `${setupPct}%` }} />
              </div>
            </CardHeader>
            <CardContent>
              {/* Numbered vertical stepper */}
              <div className="relative">
                {setupSteps.map((s, i) => {
                  const isCurrent = i === currentStepIdx;
                  const isLast = i === setupSteps.length - 1;
                  return (
                    <div key={i} className="relative flex gap-3 pb-4 last:pb-0" data-testid={`setup-step-${i}`}>
                      {!isLast && (
                        <span className={`absolute left-[15px] top-8 -bottom-0 w-0.5 ${s.done ? 'bg-emerald-400 dark:bg-emerald-600' : 'bg-slate-200 dark:bg-zinc-700'}`} />
                      )}
                      <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold transition-colors ${
                        s.done ? 'bg-emerald-500 text-white'
                          : isCurrent ? 'bg-cyan-600 text-white ring-4 ring-cyan-500/20'
                          : 'bg-slate-100 dark:bg-zinc-800 text-slate-400 dark:text-gray-500'
                      }`}>
                        {s.done ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-medium ${s.done ? 'text-slate-400 dark:text-gray-500 line-through' : 'text-slate-800 dark:text-white'}`}>{s.label}</p>
                          {isCurrent && (
                            <button onClick={() => navigate(s.link)} data-testid={`setup-step-cta-${i}`}
                              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 dark:text-cyan-400 hover:underline">
                              Continue <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {!s.done && <p className="text-xs text-slate-400 dark:text-gray-500 mt-0.5">{s.desc}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Properties Overview ─── */}
        <Card className="border-0 bg-white dark:bg-zinc-900 shadow-lg shadow-slate-200/50 dark:shadow-black/20" data-testid="properties-overview-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-800 dark:text-white">Your Properties</CardTitle>
                <p className="text-xs text-slate-400 dark:text-gray-500">{properties.length} registered</p>
              </div>
            </div>
            <Button 
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/einvoice/portal/properties')}
            >
              Manage Properties
            </Button>
          </CardHeader>
          <CardContent>
            {properties.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {properties.slice(0, 3).map(prop => (
                  <div 
                    key={prop.id} 
                    className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-zinc-800/50 dark:to-zinc-800/30 rounded-xl border border-slate-200/50 dark:border-zinc-700/50"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-blue-500/20">
                        {prop.logo_url ? (
                          <img src={prop.logo_url} alt={prop.property_name} className="w-full h-full object-cover" />
                        ) : (
                          <Building2 className="w-7 h-7 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 dark:text-white">{prop.property_name}</p>
                        <p className="text-xs text-slate-400 dark:text-gray-500 font-mono">{prop.property_code}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 dark:text-gray-400">Status</span>
                      <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0">
                        Active
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-slate-400 dark:text-gray-500">No properties added yet</p>
                <Button 
                  size="sm" 
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate('/einvoice/portal/properties')}
                >
                  Add Your First Property
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
